import React, { useState, useEffect } from "react";
import { Form, ProgressBar } from "react-bootstrap";
import { connect } from "react-redux";
import api from "api";
import AppModal from "components/ui/AppModal/AppModal";
import { clearProjectCache } from "utils/authUtils";
import "./ProjectRegistration.css";

function ProjectRegistration({ token, email }) {
  const [nome, setNome] = useState("");
  const [produto, setProduto] = useState("");
  const [dataProjeto, setDataProjeto] = useState("");
  const [arquivos, setArquivos] = useState([]);
  const [mensagemErro, setMensagemErro] = useState("");
  const [loading, setLoading] = useState(false);
  const [maxStorage, setMaxStorage] = useState(null);
  const [usedStorage, setUsedStorage] = useState(null);
  const [upgradeModalOpen, setUpgradeModalOpen] = useState(false);
  const [projectCreatedModalOpen, setProjectCreatedModalOpen] = useState(false);
  const [errorModal, setErrorModal] = useState({
    open: false,
    title: "",
    message: ""
  });

  const GB_TO_BYTES = 1073741824; // 1 GB em bytes

  useEffect(() => {
    const fetchStorageData = async () => {
      try {
        const [userPlanResponse, azureUsageResponse] = await Promise.all([
          api.get("/plans/user", {
            headers: {
              Authorization: `Bearer ${token}`
            }
          }),
          api.get("/plans/usage", {
            headers: {
              Authorization: `Bearer ${token}`
            }
          })
        ]);

        const fetchedPlanData = userPlanResponse.data[0];
        const fetchedAzureUsageData = azureUsageResponse.data;

        setMaxStorage((fetchedPlanData.storage || 0) * GB_TO_BYTES);
        setUsedStorage((fetchedAzureUsageData.total_size || 0) * GB_TO_BYTES);
      } catch (error) {
        console.error("Erro ao buscar os dados de armazenamento:", error);
      }
    };
    fetchStorageData();
  }, [token]);

  const handleClear = () => {
    setNome("");
    setProduto("");
    setDataProjeto("");
    setArquivos([]);
    setMensagemErro("");
  };

  const closeErrorModal = () =>
    setErrorModal(prev => ({ ...prev, open: false }));

  const handleUpgradeConfirm = () => {
    setUpgradeModalOpen(false);
    window.location.href = "/plans";
  };

  const handleProjectCreatedDone = () => {
    setProjectCreatedModalOpen(false);
    clearProjectCache(email);
    handleClear();
    window.location.href = "/projects";
  };

  const handleFileChange = e => {
    const novosArquivos = Array.from(e.target.files);
    let erroMensagem = "";

    const tamanhoMaximoVideoIndividual = 300 * 1024 * 1024; // 300 MB
    const tamanhoMaximoTotalVideos = 900 * 1024 * 1024; // 900 MB
    const tamanhoMaximoLaz = 100 * 1024 * 1024; // 100MB
    const tamanhoMaximoOutros = 1024 * 1024 * 1024; // 1GB

    const arquivosValidos = [];

    if (produto === "filmagens") {
      let tamanhoTotalAtual = calcularTamanhoArquivos();

      for (const novoArquivo of novosArquivos) {
        if (novoArquivo.size > tamanhoMaximoVideoIndividual) {
          erroMensagem = `O vídeo ${novoArquivo.name} excede o limite de 300MB.`;
          break;
        }
        if (tamanhoTotalAtual + novoArquivo.size > tamanhoMaximoTotalVideos) {
          erroMensagem = `O total dos vídeos excede o limite de 900MB.`;
          break;
        }
        tamanhoTotalAtual += novoArquivo.size;
        arquivosValidos.push(novoArquivo);
      }
    } else {
      novosArquivos.forEach(novoArquivo => {
        const extensaoArquivo = novoArquivo.name
          .split(".")
          .pop()
          .toLowerCase();
        if (
          (extensaoArquivo === "laz" || extensaoArquivo === "las") &&
          novoArquivo.size > tamanhoMaximoLaz
        ) {
          erroMensagem = `O arquivo ${novoArquivo.name} excede o limite de 100MB para arquivos LAS/LAZ.`;
        } else if (novoArquivo.size > tamanhoMaximoOutros) {
          erroMensagem = `O arquivo ${novoArquivo.name} excede o limite de 1GB.`;
        } else {
          arquivosValidos.push(novoArquivo);
        }
      });
    }

    setMensagemErro(erroMensagem);

    if (!erroMensagem && arquivosValidos.length > 0) {
      setArquivos(prevArquivos => [...prevArquivos, ...arquivosValidos]);
    }
  };

  const calcularTamanhoArquivos = () => {
    return arquivos.reduce((total, arquivo) => total + arquivo.size, 0);
  };

  const handleSubmit = async e => {
    e.preventDefault();

    if (!nome || !produto || !dataProjeto || arquivos.length === 0) {
      setMensagemErro("Todos os campos são obrigatórios.");
      return;
    }

    const tamanhoTotalUpload = calcularTamanhoArquivos();

    if (maxStorage !== null && usedStorage !== null) {
      const armazenamentoNecessario = usedStorage + tamanhoTotalUpload;

      if (armazenamentoNecessario > maxStorage) {
        setUpgradeModalOpen(true);
        return;
      }
    } else {
      setMensagemErro(
        "Erro ao carregar dados de armazenamento. Tente novamente."
      );
      return;
    }

    setLoading(true);

    const formData = new FormData();
    formData.append("name", nome);
    formData.append("product", produto);
    formData.append("date", dataProjeto);
    arquivos.forEach(arquivo => formData.append("files", arquivo));

    try {
      const response = await api.post("/projects/create", formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "multipart/form-data"
        }
      });
      if (response.status === 201) {
        setProjectCreatedModalOpen(true);
      }
    } catch (error) {
      setMensagemErro("Erro ao enviar o projeto. Tente novamente.");
      setErrorModal({
        open: true,
        variant: "error",
        title: "Erro ao criar o projeto",
        message: "Por favor, verifique os dados e tente novamente."
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      style={{
        height: "calc(100vh - 50px)",
        display: "flex",
        flexDirection: "column"
      }}
    >
      <div>
        <h3 className="mb-2 text-center">Criar Projeto</h3>
        <p className="text-muted text-center mx-3">
          Insira as informações abaixo e cadastre ou visualize seu projeto.
        </p>
        <hr className="m-0" />
      </div>

      <div>
        <Form className="m-3" onSubmit={handleSubmit}>
          <div className="card-style rounded p-3">
            <Form.Group className="m-3">
              <Form.Label>Nome</Form.Label>
              <Form.Control
                type="text"
                placeholder="Digite um nome"
                value={nome}
                onChange={e => setNome(e.target.value)}
              />
            </Form.Group>

            <Form.Group className="m-3">
              <Form.Label>Selecionar Produto</Form.Label>
              <Form.Control
                as="select"
                aria-label="Selecionar Produto"
                value={produto}
                onChange={e => setProduto(e.target.value)}
              >
                <option value="">Selecionar Produto</option>
                <option value="filmagens">Filmagens</option>
                <option value="gemeo_digital">Gêmeo Digital</option>
                <option value="tour_360">Tour Virtual</option>
                <option value="fotos">Fotos</option>
              </Form.Control>

              {produto === "filmagens" && (
                <p className="text-muted mt-2" style={{ fontSize: "0.875rem" }}>
                  <strong>Dica:</strong> Para melhor performance e visualização,
                  envie vídeos com resolução máxima de 1080p (Full HD). O limite
                  é de 300MB por vídeo e 900MB no total por projeto.
                </p>
              )}
            </Form.Group>

            <Form.Group className="m-3">
              <Form.Label>Data do Projeto</Form.Label>
              <Form.Control
                type="date"
                value={dataProjeto}
                onChange={e => setDataProjeto(e.target.value)}
              />
            </Form.Group>

            <Form.Group className="m-3">
              <Form.Label>Arquivo</Form.Label>
              <div>
                <input
                  type="file"
                  multiple
                  onChange={handleFileChange}
                  style={{ display: "none" }}
                  id="file-input"
                  className="custom-button"
                />
                <label
                  htmlFor="file-input"
                  className="custom-button-archive btn me-2 rounded"
                >
                  Escolher Arquivos
                </label>
                {arquivos.length > 0 && (
                  <div>
                    <strong>Arquivos selecionados:</strong>
                    <ul>
                      {arquivos.map((arquivo, index) => (
                        <li key={index}>{arquivo.name}</li>
                      ))}
                    </ul>
                  </div>
                )}
                {mensagemErro && <p className="text-danger">{mensagemErro}</p>}
              </div>
            </Form.Group>

            {loading && (
              <div className="m-3">
                <ProgressBar animated now={100} label="Enviando..." />
              </div>
            )}

            <div className="d-flex justify-content-between m-3">
              <button
                className="btn rounded-pill bg-secondary"
                type="button"
                title="Limpar"
                onClick={handleClear}
              >
                <i className="fas fa-trash me-2 text-white" />
              </button>
              <button
                className="rounded-pill custom-button"
                type="submit"
                disabled={loading}
              >
                {loading ? (
                  "Enviando..."
                ) : (
                  <>
                    <i className="fas fa-paper-plane me-2" /> Enviar
                  </>
                )}
              </button>
            </div>
          </div>
        </Form>
      </div>

      <AppModal
        isOpen={upgradeModalOpen}
        onClose={() => setUpgradeModalOpen(false)}
        allowOutsideClick={false}
        variant="warning"
        title="Espaço insuficiente"
        message="Seu plano não permite o upload de arquivos com esse tamanho. Considere fazer um upgrade."
        confirmText="Ver Planos"
        cancelText="Cancelar"
        onConfirm={handleUpgradeConfirm}
        onCancel={() => setUpgradeModalOpen(false)}
      />

      <AppModal
        isOpen={projectCreatedModalOpen}
        onClose={handleProjectCreatedDone}
        allowOutsideClick={true}
        variant="success"
        title="Projeto criado com sucesso!"
        message={null}
        confirmText="OK"
        cancelText={null}
        onConfirm={handleProjectCreatedDone}
        onCancel={handleProjectCreatedDone}
      />

      <AppModal
        isOpen={errorModal.open}
        onClose={closeErrorModal}
        allowOutsideClick={true}
        variant={errorModal.variant}
        title={errorModal.title}
        message={errorModal.message}
        confirmText="OK"
        cancelText={null}
        onConfirm={closeErrorModal}
        onCancel={closeErrorModal}
      />
    </div>
  );
}

const mapStateToProps = state => ({
  token: state.auth.accessToken,
  email: state.auth.email
});

export default connect(mapStateToProps)(ProjectRegistration);

